import { useState, useEffect } from "react";
import { fieldLabelClass } from "@/styles/variants";
import { ui } from "@/strings";

type GradeBand = "elementary" | "middle" | "high" | "all" | "custom";

interface GradeRangeSelectorProps {
  value: number[] | null;
  onChange: (grades: number[] | null) => void;
  label?: string;
}

const GRADE_BANDS: Record<GradeBand, number[] | null> = {
  elementary: [3, 4, 5],
  middle: [6, 7, 8],
  high: [9, 10, 11, 12],
  all: null,
  custom: [], // Will be determined by checkboxes
};

const ALL_GRADES = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function getInitialBand(grades: number[] | null): GradeBand {
  if (grades === null) return "all";
  if (grades.length === 0) return "custom";

  const sorted = [...grades].sort((a, b) => a - b);

  // Check if it matches a preset band
  if (JSON.stringify(sorted) === JSON.stringify([3, 4, 5])) return "elementary";
  if (JSON.stringify(sorted) === JSON.stringify([6, 7, 8])) return "middle";
  if (JSON.stringify(sorted) === JSON.stringify([9, 10, 11, 12])) return "high";

  return "custom";
}

export function GradeRangeSelector({ value, onChange, label = ui.labels.gradeRange }: GradeRangeSelectorProps) {
  const [selectedBand, setSelectedBand] = useState<GradeBand>(() => getInitialBand(value));
  const [customGrades, setCustomGrades] = useState<number[]>(() => {
    if (value === null) return [];
    return value;
  });

  // Sync internal state when value prop changes (e.g., modal reopens with different data)
  useEffect(() => {
    setSelectedBand(getInitialBand(value));
    setCustomGrades(value === null ? [] : value);
  }, [value]);

  const handleBandChange = (band: GradeBand) => {
    setSelectedBand(band);

    if (band === "custom") {
      // Keep existing custom selection, or null if empty
      onChange(customGrades.length > 0 ? customGrades : null);
    } else {
      onChange(GRADE_BANDS[band]);
    }
  };

  const handleCustomGradeToggle = (grade: number) => {
    const newGrades = customGrades.includes(grade)
      ? customGrades.filter((g) => g !== grade)
      : [...customGrades, grade].sort((a, b) => a - b);

    setCustomGrades(newGrades);
    onChange(newGrades.length > 0 ? newGrades : null);
  };

  return (
    <div className="space-y-3">
      <label className={fieldLabelClass}>{label}</label>

      {/* Segmented Control */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => handleBandChange("elementary")}
          className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
            selectedBand === "elementary"
              ? "bg-primary text-white border-primary"
              : "bg-white text-text-theme-primary border-border hover:bg-surface-secondary"
          }`}
        >
          {ui.labels.elementaryBand}
        </button>

        <button
          type="button"
          onClick={() => handleBandChange("middle")}
          className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
            selectedBand === "middle"
              ? "bg-primary text-white border-primary"
              : "bg-white text-text-theme-primary border-border hover:bg-surface-secondary"
          }`}
        >
          {ui.labels.middleBand}
        </button>

        <button
          type="button"
          onClick={() => handleBandChange("high")}
          className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
            selectedBand === "high"
              ? "bg-primary text-white border-primary"
              : "bg-white text-text-theme-primary border-border hover:bg-surface-secondary"
          }`}
        >
          {ui.labels.highBand}
        </button>

        <button
          type="button"
          onClick={() => handleBandChange("all")}
          className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
            selectedBand === "all"
              ? "bg-primary text-white border-primary"
              : "bg-white text-text-theme-primary border-border hover:bg-surface-secondary"
          }`}
        >
          {ui.labels.allGrades}
        </button>

        <button
          type="button"
          onClick={() => handleBandChange("custom")}
          className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
            selectedBand === "custom"
              ? "bg-primary text-white border-primary"
              : "bg-white text-text-theme-primary border-border hover:bg-surface-secondary"
          }`}
        >
          {ui.labels.custom}
        </button>
      </div>

      {/* Custom Grade Checkboxes (shown when Custom is selected) */}
      {selectedBand === "custom" && (
        <div className="pl-4 pt-2 border-l-2 border-brand-200">
          <p className="text-sm text-text-theme-secondary mb-2">{ui.labels.selectSpecificGrades}</p>
          <div className="flex gap-2 flex-wrap">
            {ALL_GRADES.map((grade) => (
              <label
                key={grade}
                className="flex items-center gap-2 px-3 py-1.5 rounded border border-border cursor-pointer hover:bg-surface-secondary transition-colors"
              >
                <input
                  type="checkbox"
                  checked={customGrades.includes(grade)}
                  onChange={() => handleCustomGradeToggle(grade)}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-text-theme-primary">{grade}</span>
              </label>
            ))}
          </div>
          {customGrades.length === 0 && (
            <p className="text-sm text-semantic-warning mt-2">{ui.labels.selectAtLeastOneGrade}</p>
          )}
        </div>
      )}
    </div>
  );
}
